import { FC, useMemo, useState } from 'react';
import { AwaitContainer } from '@ibsys/kit';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { type BillingDateRange, useBilling } from '@/entities/billing';
import { PageContainer } from '@/shared/ui';
import { BillingDateFilter } from './BillingDateFilter';
import { BillingStats } from './BillingStats';
import { DailyUsageChart } from './DailyUsageChart';

const ISO_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Страница биллинга. Container: владеет состоянием диапазона дат,
 * получает отфильтрованную сводку через `useBilling` и пробрасывает её
 * презентерам — `BillingStats` и `DailyUsageChart`.
 */
export const BillingPage: FC = () => {
  const { t } = useTranslation('billing');

  // Дефолтный диапазон — текущий месяц целиком; вычисляется один раз при монтировании.
  const [dateRange, setDateRange] = useState<BillingDateRange>(() => {
    const today = new Date();
    return {
      from: format(startOfMonth(today), ISO_DATE_FORMAT),
      to: format(endOfMonth(today), ISO_DATE_FORMAT),
    };
  });

  const { data, isLoading } = useBilling(dateRange);

  // Фильтр выдаёт строки — пустые значения не должны ронять страницу.
  const isRangeValid = useMemo(
    () => Boolean(dateRange.from) && Boolean(dateRange.to) && dateRange.from <= dateRange.to,
    [dateRange],
  );

  return (
    <PageContainer>
      <AwaitContainer isLoading={isLoading} className="flex flex-col gap-6">
        <h1 className="text-2xl font-bold">{t('pageTitle')}</h1>
        <BillingDateFilter value={dateRange} onChange={setDateRange} />
        {data && isRangeValid && (
          <>
            <BillingStats consumedMinutes={data.consumedMinutes} />
            <DailyUsageChart items={data.dailyUsage} />
          </>
        )}
      </AwaitContainer>
    </PageContainer>
  );
};
